import { Program } from "@/types/program-viewer";
import { Json } from "@/integrations/supabase/types";

/**
 * Extracts program name from various possible locations in the Supabase response
 */
export function extractProgramName(
  programData: Record<string, any> | Json | null,
  aiContext: Record<string, any> | Json | null,
  name: string | null
): string {
  // Check if programData is an object and has a name property
  const hasNameInProgramData = 
    programData && 
    typeof programData === 'object' && 
    programData !== null &&
    !Array.isArray(programData) && 
    'name' in programData;
  
  // Check if aiContext is an object with basic_info.name
  const hasNameInAiContext = 
    aiContext && 
    typeof aiContext === 'object' && 
    aiContext !== null &&
    !Array.isArray(aiContext) && 
    'basic_info' in aiContext &&
    typeof aiContext.basic_info === 'object' &&
    aiContext.basic_info !== null &&
    'name' in aiContext.basic_info;
  
  // Return the first available name source
  if (hasNameInProgramData) {
    return (programData as Record<string, any>).name;
  } else if (hasNameInAiContext) {
    return (aiContext as Record<string, any>).basic_info.name;
  } else if (name) {
    return name;
  } else {
    return "Unnamed Program";
  }
}

/**
 * Extracts the program overview from various possible locations in the data
 */
export function extractProgramOverview(
  programData: Record<string, any> | Json | null
): string | undefined {
  if (!programData) return undefined;
  
  console.log("Extracting overview from data structure:", JSON.stringify(programData, null, 2));
  
  // Direct access to overview field
  if (typeof programData === 'object' && 
      programData !== null &&
      !Array.isArray(programData) && 
      'overview' in programData &&
      typeof programData.overview === 'string' &&
      programData.overview.trim() !== '') {
    console.log("Found direct overview field:", programData.overview);
    return programData.overview;
  }
  
  // Look for description field
  if (typeof programData === 'object' && 
      programData !== null &&
      !Array.isArray(programData) && 
      'description' in programData &&
      typeof programData.description === 'string' &&
      programData.description.trim() !== '') {
    console.log("Found description field:", programData.description);
    return programData.description;
  }
  
  // Look in data field if it exists
  if (typeof programData === 'object' && 
      programData !== null &&
      !Array.isArray(programData) && 
      'data' in programData &&
      typeof programData.data === 'object' &&
      programData.data !== null) {
    
    // Check for overview in data
    if ('overview' in programData.data && 
        typeof programData.data.overview === 'string' &&
        programData.data.overview.trim() !== '') {
      console.log("Found overview in data field:", programData.data.overview);
      return programData.data.overview;
    }
    
    // Check for description in data
    if ('description' in programData.data && 
        typeof programData.data.description === 'string' &&
        programData.data.description.trim() !== '') {
      console.log("Found description in data field:", programData.data.description);
      return programData.data.description;
    }
  }
  
  // Check in program_data field if it exists
  if (typeof programData === 'object' && 
      programData !== null &&
      !Array.isArray(programData) && 
      'program_data' in programData &&
      typeof programData.program_data === 'object' &&
      programData.program_data !== null) {
    
    // Check for overview in program_data
    if ('overview' in programData.program_data && 
        typeof programData.program_data.overview === 'string' &&
        programData.program_data.overview.trim() !== '') {
      console.log("Found overview in program_data field:", programData.program_data.overview);
      return programData.program_data.overview;
    }
    
    // Check for description in program_data
    if ('description' in programData.program_data && 
        typeof programData.program_data.description === 'string' &&
        programData.program_data.description.trim() !== '') {
      console.log("Found description in program_data field:", programData.program_data.description);
      return programData.program_data.description;
    }
  }
  
  // Check for justification with overview
  if (typeof programData === 'object' && 
      programData !== null &&
      !Array.isArray(programData) && 
      'justification' in programData &&
      typeof programData.justification === 'object' &&
      programData.justification !== null &&
      'overview' in programData.justification) {
    console.log("Found overview in justification:", programData.justification.overview);
    return programData.justification.overview as string;
  }
  
  // Look for nested plan with description
  if (typeof programData === 'object' && 
      programData !== null &&
      !Array.isArray(programData) && 
      'plan' in programData &&
      typeof programData.plan === 'object' &&
      programData.plan !== null &&
      'description' in programData.plan) {
    console.log("Found description in plan:", programData.plan.description);
    return programData.plan.description as string;
  }
  
  // Check any top-level key that might contain nested objects with overview or description
  if (typeof programData === 'object' && programData !== null && !Array.isArray(programData)) {
    for (const key in programData) {
      const value = programData[key];
      if (typeof value === 'object' && 
          value !== null && 
          !Array.isArray(value)) {
          
        // Check for overview in nested object
        if ('overview' in value && typeof value.overview === 'string' && value.overview.trim() !== '') {
          console.log(`Found overview in ${key}:`, value.overview);
          return value.overview as string;
        }
        
        // Check for description in nested object
        if ('description' in value && typeof value.description === 'string' && value.description.trim() !== '') {
          console.log(`Found description in ${key}:`, value.description);
          return value.description as string;
        }
        
        // Check for nested justification with overview
        if ('justification' in value && 
            typeof value.justification === 'object' && 
            value.justification !== null &&
            'overview' in value.justification) {
          console.log(`Found overview in ${key}.justification:`, value.justification.overview);
          return value.justification.overview as string;
        }
      }
    }
  }
  
  console.log("Could not find overview in program data");
  return undefined;
}

/**
 * Creates an empty program structure
 */
export function createEmptyProgram(programId: string, programName: string): Program {
  return {
    id: programId,
    name: programName,
    weeks: []
  };
}
